import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest, map } from 'rxjs';

import { DacRepository } from '@/store/dac-state/dac-state.repository';
import { DynaDocDetails, DynaFieldWithDetails, DynaFieldType } from '@/types';
import { getSingleFieldObjectFactory } from '@/components/form-builder/utils/single-field-factory';
import { FieldVariant } from '@/components/form-builder/form-builder.types';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { DacTransformationService } from '../dac-transformation.service';
import { TableDisplayComponent } from './table-view.component';

@Component({
  selector: 'sm-dac-validate-step',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormBuilderComponent, TableDisplayComponent],
  templateUrl: './dac-validate-step.component.html',
  styleUrl: './dac-validate-step.component.scss',
})
export class DacValidateStepComponent {
  dacTransformationService = inject(DacTransformationService);
  dacRepository = inject(DacRepository);
  @Input() documents: DynaDocDetails[] = [];

  checkboxAtomicForm = getSingleFieldObjectFactory({
    label: '',
    variant: FieldVariant.CHECKBOX,
    isRequired: false,
    props: {
      options: [{ code: 'agree', name: '' }],
      values: this.dacRepository.getObservable<boolean>('dacHasRightToSubmit').pipe(map(v => (v ? ['agree'] : []))),
      onCheckChange: (selected: string[]) => this.onAgreeChange(selected.includes('agree')),
    },
  });

  hasErrorBorder$ = combineLatest([
    this.dacRepository.getObservable<boolean>('dacHasRightToSubmit'),
    this.dacRepository.getObservable<boolean>('dacHasRightToSubmitTouched'),
  ]).pipe(map(([checked, touched]) => touched && !checked));

  onAgreeChange(checked: boolean) {
    this.dacRepository.set('dacHasRightToSubmit', checked);
    this.dacRepository.set('dacHasRightToSubmitTouched', true);
  }

  isCurrentFormValid(index: number): boolean {
    const required = this.documents[index].dynaFields.filter(f => f.status === 'ACTIF');
    return required.every(f => this.getSyncFieldDetails(f.dynaFieldsId).some(v => !!v.fieldValue));
  }

  getSyncFieldDetails(id: number): { fieldValue: string; [k: string]: any }[] {
    let snapshot: { fieldValue: string; [k: string]: any }[] = [];
    this.dacRepository
      .getObservableFieldDetails(id)
      .subscribe(v => (snapshot = v))
      .unsubscribe();
    return snapshot;
  }

  private getSyncDetails(id: number): any[] {
    let snapshot: any[] = [];
    this.dacRepository
      .getObservableFieldDetails(id)
      .subscribe(v => (snapshot = v))
      .unsubscribe();
    return snapshot;
  }

  getListString(id: number): string {
    return this.getSyncDetails(id)
      .map(d => d.fieldValue)
      .join(', ');
  }

  getTableData(id: number) {
    const field = this.documents.flatMap(d => d.dynaFields).find(f => f.dynaFieldsId === id);
    if (!field) return { rows: [], columns: [] };

    const structure = this.dacTransformationService.getTableStructure(field);
    const details = this.getSyncDetails(id).sort((a, b) => {
      if (a.rowId !== b.rowId) return a.rowId - b.rowId; // sort by row
      return a.columnPos - b.columnPos; // then by column
    });
    const columns = Object.entries(structure)
      .map(([key, def]: [string, any]) => ({
        key,
        label: def.label,
        type: def.type,
        columnPos: def.columnPos,
      }))
      .sort((a, b) => a.columnPos - b.columnPos);
    const cols = columns.map(c => c.key);

    // Build rows array with correct column keys
    const rows: Record<string, any>[] = [];
    const numRows = Math.ceil(details.length / cols.length);

    for (let rowIndex = 0; rowIndex < numRows; rowIndex++) {
      const row: Record<string, any> = {};
      for (let colIndex = 0; colIndex < cols.length; colIndex++) {
        const detailIndex = rowIndex * cols.length + colIndex;
        row[cols[colIndex]] = details[detailIndex]?.fieldValue ?? '';
      }
      rows.push(row);
    }
    return { rows, columns };
  }
  getTableKeys(id: number): string[] {
    const table = this.getTableData(id);
    return table.columns.map(c => c.key);
  }

  getTableLabels(id: number): string[] {
    const table = this.getTableData(id);
    return table.columns.map(c => c.label);
  }

  getSingleValue(field: DynaFieldWithDetails): string {
    if ([DynaFieldType.LIST, DynaFieldType.TABLE].includes(field.type)) return '';
    let val = '';
    this.dacRepository
      .getObservableFieldValue(field.dynaFieldsId)
      .subscribe(v => (val = v))
      .unsubscribe();
    return val;
  }

  DynaFieldType = DynaFieldType;
}
